import {
    PLUS,
    MINUS,
    TIMES,
    SLASH,
    PERCENT,
    HAT,
    NUMBER,
    STRING,
    FORMAT_AS_FLOAT,
    FORMAT_AS_RATIO,
} from './lex.js';
import { SOLO_OUTPUT } from './parse.js';

import {
    FC_String,
    FC_Number,
    nativeMath,
    nativeString,
    nativeList,
    nativeKernel,
    LIST,
} from './standardLibrary.js'

const FC_Error = Error

let stepLogIndentation = 0
const stepLog = (thing, ...whatever) => { window.postALog(`[step] ${" ".repeat(stepLogIndentation * 2)}${thing}`, ...whatever); }

const reduceFraction = (node) => {
    if (node === null) { return node }

    if (node.numerator === undefined || node.denominator === undefined) {
        return node
    }

    let greatestCommonFactor = 1
    for(let i = 0; i <= node.denominator; i += 1) {
        if (node.denominator % i === 0 && node.numerator % i === 0) {
            greatestCommonFactor = i
        }
    }
    
    return FC_Number(node.numerator / greatestCommonFactor, node.denominator / greatestCommonFactor)
}

const unaryOp = (token) => {
    switch (token.type) {
        case PLUS: return (a) => FC_Number((0 + a.numerator), a.denominator)
        case MINUS: return (a) => FC_Number((0 - a.numerator), a.denominator)
        default:
            throw new FC_Error(`i don't know that token: ${token.type}`)
    }
}

const binaryOp = (token) => {
    switch (token.type) {
        case PLUS: return nativeMath.add;
        case MINUS: return nativeMath.subtract;
        case TIMES: return nativeMath.multiply;
        case SLASH: return nativeMath.divide;
        case PERCENT: return nativeMath.modulo;
        case HAT: return nativeMath.exponent;
        default:
            throw new FC_Error(`i don't know that token: ${token.type}`)
    }
}

const defaultWorldState = {
    nativeFunctions: {
        Math: nativeMath,
        String: nativeString,
        List: nativeList,
        Kernel: nativeKernel,
    },
    userFunctions: {},
    variables: {},
}

const HIDDEN = "HIDDEN"

export const interpret = (statementList, inputWorldState = defaultWorldState, finalExpression) => {
    let worldState = {
        nativeFunctions: {...inputWorldState.nativeFunctions },
        userFunctions: { ...inputWorldState.userFunctions },
        variables: { ...inputWorldState.variables },
    }
    
    const doFunctionInvocation = (oldNode) => {
        console.log("INVOCATION BEGINS")

        let fn = worldState.nativeFunctions

        console.log('invocation path', oldNode.path)
        
        oldNode.path.forEach((p) => {
            fn = fn?.[p]
        })

        let userFn = worldState.userFunctions

        oldNode.path.forEach((p) => {
            userFn = userFn?.[p]
        })

        console.log("fn", fn)
        console.log("userFn", userFn)
        
        if (fn || userFn) {
            const evaluatedArgs = oldNode.args.map((arg) => {
                return innerInterpret(arg)
            })
            
            if (fn) {
                return fn(...evaluatedArgs)
            } else if (userFn) {
                let varsIncludingArgs = { ...worldState.variables }

                evaluatedArgs.forEach((value, index) => {
                    varsIncludingArgs[`$${index + 1}`] = value
                })

                console.log(JSON.stringify(varsIncludingArgs))

                return interpret(userFn.bodyStatements, {
                    ...worldState,
                    variables: varsIncludingArgs,
                }, userFn.returnExpression)
            }
        }
        throw new FC_Error(`GLOBAL_NOT_FOUND function ${oldNode.path.join(".")} isn't in the stdlib nor is it defined`)
    }

    const innerInterpret = (oldNode) => {
        try {
            let newNode = null
            stepLogIndentation += 1
            stepLog(`switching on '${oldNode.type}'`)
            switch (oldNode.type) {
                case "literal-string": 
                    newNode = FC_String(oldNode.body)
                    stepLog(`literal: "${oldNode.body}"`)
                    break
                case "literal-number":
                    newNode = FC_Number(oldNode.numerator, oldNode.denominator)
                    stepLog(`literal: ${oldNode.numerator}/${oldNode.denominator}`)
                    break
                case "unary": 
                    newNode = unaryOp(oldNode.operator)(innerInterpret(oldNode.right))
                    break
                case "binary":
                    const left = innerInterpret(oldNode.left)
                    const right = innerInterpret(oldNode.right)
                    stepLog(`performing binary operation: ${oldNode.operator.type}`)
                    newNode = binaryOp(oldNode.operator)(left, right)
                    break
                case "grouping": 
                    newNode = innerInterpret(oldNode.value)
                    break
                case "storage":
                    const value = innerInterpret(oldNode.value)
                    stepLog(`storing in '${ oldNode.name.value}'`)
                    if (worldState.variables[oldNode.name.value]?.value === undefined) {
                        worldState.variables[oldNode.name.value] = { ...worldState.variables[ oldNode.name.value], value }
                    } else {
                        throw new FC_Error(`variable "${ oldNode.name.value}" already has a value`)
                    }
                    newNode = value
                    break
                case "retrieval":
                    console.log({where: "retrieval", worldState})
                    const retrievedNode = worldState.variables[oldNode.name]

                    let retrievedValue = retrievedNode?.value || (() => {
                        switch (retrievedNode?.type) {
                            case "STRING": 
                                return FC_String(retrievedNode.body)
                            case "NUMBER":
                                return  FC_Number(retrievedNode.numerator, retrievedNode.denominator)
                            default:
                                throw new FC_Error(`GLOBAL_NOT_FOUND: can't retrieve node: ${JSON.stringify(retrievedNode)}`)
                        }
                    })()
                    
                    stepLog(`retrieved ${JSON.stringify(retrievedValue)}`)
                    if (retrievedValue === undefined) {
                        throw new FC_Error(`GLOBAL_NOT_FOUND: ${oldNode.name}`)
                    }
                    newNode = retrievedValue
                    break
                case "invocation":
                    newNode = doFunctionInvocation(oldNode)
                    break
                case "hide":
                    if (worldState.variables[oldNode.name]?.hidden !== HIDDEN) {
                        worldState.variables[oldNode.name] = { ...worldState.variables[oldNode.name], hidden: HIDDEN }
                    } else {
                        throw new FC_Error(`global "${oldNode.name}" is already hidden`)
                    }
                    break
                case "format":
                    if (worldState.variables[oldNode.name]?.format !== undefined) {
                        throw new FC_Error(`global "${oldNode.name}" is already formatted`)
                    } else {
                        worldState.variables[oldNode.name] = { ...worldState.variables[oldNode.name], format: oldNode.style }
                    }
                    break
                case "function":
                    if (worldState.userFunctions[oldNode.name]) {
                        throw new FC_Error(`function ${oldNode.name} already exists`)
                    }
                    worldState.userFunctions[oldNode.name] = oldNode
                    break;
                default:
                    throw new FC_Error(`i don't know that node type: ${oldNode.type}`)
            }
            return reduceFraction(newNode)
        } catch(e) {
            throw e
        } finally {
            stepLogIndentation -= 1
        }
    }

    let list = [...statementList]
    let maxAttempts = Math.pow(list.length, 2)
    let attempts = 0
    while (list.length > 0) {
        if (attempts > maxAttempts) {
            throw new FC_Error("EXCEEDED MAX ATTEMPTS")
        } else {
            attempts += 1
        }

        stepLog(`attempt ${attempts} of ${maxAttempts}`)
        stepLog(`vars: ${JSON.stringify(Object.keys(worldState.variables))}`)
        stepLog(`userFuns: ${JSON.stringify(Object.keys(worldState.userFunctions))}`)

        const item = list.shift()
        try {
            stepLog(`beginning recursion`)
            innerInterpret(item)
            stepLog(`ending recursion`)
        } catch (e) {
            if (e.message.startsWith("GLOBAL_NOT_FOUND")) {
                stepLog(`couldn't find a global: ${e}`)
                list.push(item)
            } else {
                return { error: e.message }
            }
        }
    }

    if (finalExpression) {
        return innerInterpret(finalExpression)
    }

    stepLog(`vars: ${JSON.stringify(Object.keys(worldState.variables))}`)
    return worldState
}

export const printOutput = ({ error, variables }) => {
    if (error !== null && error !== undefined) {
        return error
    }
    if (variables === null || variables === undefined) {
        throw new FC_Error(`not sure why, but variables is ${JSON.stringify(variables)}`)
    }

    return Object.entries(variables)
        .flatMap(([globalName, { value, hidden, format }]) => {
            const soloOutput = globalName === SOLO_OUTPUT

            if (hidden === HIDDEN) {
                stepLog(`skipping output of hidden storage: ${globalName}`)
                return []
            }

            const printed = printSimple(globalName, value, format, soloOutput)
            if (printed) { return printed }

            if (value.type === LIST) {
                const formatted_list = value.value.map((i) => {
                    const formatted_item = printSimple(null, i, null, true)
                    return `  * ${formatted_item}`
                }).join("\n")
                const rendered = `list of length ${value.value.length}\n${formatted_list}\n`

                if (soloOutput) {
                    stepLog(`printing solo output as list`)
                    return [`${rendered}`]
                } else {
                    stepLog(`printing list output for ${globalName}`)
                    return [`${globalName}: ${rendered}`]
                }   
            }

            throw new FC_Error(`unknown global type: ${globalName} :: ${JSON.stringify(value)}`)
        }
    )
    .join("\n")
}

const printSimple = (globalName, value, format, soloOutput) => {
    if (value.type === STRING) {
        if (soloOutput) {
            stepLog(`printing solo output as string`)
            return [`"${value.body}"`]
        } else {
            stepLog(`printing string output for ${globalName}`)
            return [`${globalName}: "${value.body}"`]
        }
    }

    if (value.type === NUMBER) {
        let rendered = null
        switch (format) {
            case FORMAT_AS_FLOAT:
            rendered = `${value.numerator / value.denominator}`
            break
        case FORMAT_AS_RATIO:
            rendered = `${value.numerator}/${value.denominator}`
            break
        default:
            rendered = `${value.numerator / value.denominator}`
            break
        }

        if (soloOutput) {
            stepLog(`printing solo output as number`)
            return [`${rendered}`]
        } else {
            stepLog(`printing number output for ${globalName}`)
            return [`${globalName}: ${rendered}`]
        }   
    }

    return false
}